import type { ConsoleEntry } from '../types'

type ConsoleLevel = ConsoleEntry['level']

const LEVELS: ConsoleLevel[] = ['error', 'warn', 'info', 'debug']

function serializeArg(arg: unknown): string {
  if (arg instanceof Error) return `${arg.name}: ${arg.message}${arg.stack ? `\n${arg.stack}` : ''}`
  if (typeof arg === 'string') return arg
  try {
    return JSON.stringify(arg)
  } catch {
    return String(arg)
  }
}

function truncate(message: string): string {
  return message.length > 1200 ? `${message.slice(0, 1200)}...` : message
}

export class ConsoleCapture {
  private entries: ConsoleEntry[] = []
  private original = new Map<ConsoleLevel, (...args: unknown[]) => void>()
  private boundError = (event: ErrorEvent): void => {
    const message = event.error ? serializeArg(event.error) : event.message
    this.add('error', message, event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : 'window.error')
  }
  private boundRejection = (event: PromiseRejectionEvent): void => {
    this.add('error', `Unhandled promise rejection: ${serializeArg(event.reason)}`, 'unhandledrejection')
  }

  constructor(private readonly limit = 50) {}

  start(): void {
    for (const level of LEVELS) {
      const original = console[level].bind(console) as (...args: unknown[]) => void
      this.original.set(level, original)
      console[level] = (...args: unknown[]) => {
        this.add(level, args.map(serializeArg).join(' '), 'console')
        original(...args)
      }
    }
    window.addEventListener('error', this.boundError)
    window.addEventListener('unhandledrejection', this.boundRejection)
  }

  stop(): void {
    for (const [level, original] of this.original) {
      console[level] = original as typeof console[typeof level]
    }
    this.original.clear()
    window.removeEventListener('error', this.boundError)
    window.removeEventListener('unhandledrejection', this.boundRejection)
  }

  recent(max = 8, maxAgeMs = 120000): ConsoleEntry[] {
    const cutoff = Date.now() - maxAgeMs
    return this.entries
      .filter(entry => Date.parse(entry.timestamp) >= cutoff)
      .filter(entry => entry.level === 'error' || entry.level === 'warn')
      .slice(-max)
  }

  private add(level: ConsoleLevel, message: string, source?: string): void {
    const normalized = truncate(message.trim())
    if (!normalized) return
    this.entries.push({
      level,
      message: normalized,
      timestamp: new Date().toISOString(),
      source,
    })
    if (this.entries.length > this.limit) {
      this.entries.splice(0, this.entries.length - this.limit)
    }
  }
}
